<template>
  <input
    ref="input"
    data-testid="input-field"
    class="read-only:text-gray-60 read-only:border-gray-20 read-only:bg-gray-20 disabled:border-gray-20 disabled:bg-gray-20 disabled:text-gray-60 text-ellipsis px-[11px] leading-6 read-only:cursor-default disabled:cursor-not-allowed"
    :class="[
      $style.input,
      { [$style.error]: error, 'bg-white': light },
      small ? 'py-[3px] text-xs' : 'py-[7px] text-sm',
    ]"
    :style="inputStyle"
    v-bind="$attrs"
    :disabled="disabled"
    :value="modelValue"
    :placeholder="placeholder"
    @input="handleInput"
  />
  <span v-if="showPlaceholderElement" ref="placeholderElement" class="invisible absolute whitespace-nowrap">
    {{ placeholder }}
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue?: string
    /**
     * apply error styles
     */
    error?: boolean
    /**
     * small version of the input
     */
    small?: boolean
    light?: boolean
    disabled?: boolean
    placeholder?: string
    shrinkablePlaceholder?: boolean
  }>(),
  {
    modelValue: '',
    error: false,
    small: false,
    light: false,
    disabled: false,
    placeholder: '',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string, event: Event]
}>()

defineOptions({
  inheritAttrs: false,
})

const placeholderElement = ref<HTMLElement | null>(null)

const input = ref<HTMLInputElement | null>(null)

const handleInput = (event: Event) => {
  if (event.target instanceof HTMLInputElement) emit('update:modelValue', event.target.value, event)
}

const showPlaceholderElement = computed(() => props.placeholder && !props.shrinkablePlaceholder)

const inputStyle = computed(() => {
  if (showPlaceholderElement.value) {
    const placeholderWidth = placeholderElement.value?.clientWidth
    return { minWidth: `calc(${placeholderWidth}px + 32px)` } // 32px is the right-padding for input
  }
  return {}
})

const focusInput = () => input.value?.focus()

defineExpose({ focus: focusInput })
</script>

<style lang="scss" module>
.input {
  width: 100%;
  border-radius: var(--border-radius);
  appearance: none;
  color: var(--gray-80);
  background-color: var(--gray-10);
  border: solid 1px var(--gray-30);
  transition:
    border-color 0.16s ease-in,
    color 0.16s ease-in,
    box-shadow 0.16s ease-in;
  font-weight: theme('fontWeight.normal');

  &::placeholder {
    color: var(--gray-50);
  }

  &:hover {
    border-color: var(--gray-40);
  }

  &:focus {
    border-color: var(--active-primary);
    box-shadow: inset 0px 0px 2px rgba(59, 76, 191, 0.6);
    outline: 0;
  }

  &[type='search']::-webkit-search-decoration,
  &[type='search']::-webkit-search-cancel-button,
  &[type='search']::-webkit-search-results-button,
  &[type='search']::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }
}

.error {
  background-color: var(--red-10);
  border-color: var(--red-40);

  &:focus {
    border-color: var(--red-50);
    box-shadow: inset 0px 0px 4px rgba(229, 62, 62, 0.6);
  }
}
</style>
